import { marketApi, Site } from '@envato/foundation-types'
import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import { fetchNewestItemsByCategory } from '../../../common/actions/home/fetchNewestItemsByCategory'
import { setUserVideoPlayerStatus } from '../../../common/actions/user/preference'
import { reportProductClick } from '../../../common/analytics/resultAnalytics'
import { IRootState } from '../../../common/reducers'
import { ILocationParams } from '../../../common/reducers/reactRouterLocation'
import { decorateSearchResult } from '../../../common/selectors/searchResults'
import { hasFeature } from '../../../common/utils/features'
import makeSearchLink from '../../../common/utils/location/makeSearchLink'
import { makeLink } from '../../../common/utils/urlHelper'

import { IPageComponent } from '../../../typings/pageComponent'
import { IOwnProps } from '../../../typings/pageBlocks'
import { IItem } from '../../../typings/searchResults'
import { ISeoMetadata } from '../../../typings/seoMetadata'
import { IPageBlock } from '../../../typings/pageBlocks'
import { IExperimentsState } from '../../../common/reducers/experiments'
import { decorateBlocks } from '../../../common/utils/pageBlocks'

export interface INewestItemsData {
  items: IItem[]
  itemsCategory: string
  itemsPendingCategory: string | undefined
  loadingCachedCategory: boolean
}

interface IFeaturedAuthor {
  author?: marketApi.IFeaturedAuthor
  matches: IItem[]
}

export interface IStateProps {
  isSignedIn: boolean
  currentSite: Site
  metadata: ISeoMetadata | undefined
  newestItemsData: INewestItemsData
  featuredItems: IItem[]
  freeItems: IItem[]
  featuredAuthor: IFeaturedAuthor
  categories?: marketApi.CategoriesResult
  preferences: any
  experiments: IExperimentsState
  experimentsReady: boolean
  reportItemClick: (index: number, item: IItem, listName: string) => any
  makeSearchLink: (params: ILocationParams, currentSite?: Site) => string
  blocks: IPageBlock[]
  blocksB: IPageBlock[]
  blocksC: IPageBlock[]
  blocksD: IPageBlock[]
}

export interface IDispatchProps {
  fetchNewestItemsByCategory: (category: string) => any
  muteVideo: () => any
}

const mapStateToProps = (state: IRootState): IStateProps => {
  const {
    common: { currentSite },
    user: { isSignedIn, preferences },
    homePage: {
      categories,
      newestItems: newestItemMatches,
      metadata,
      newestItemsCategory,
      newestItemsPendingCategory,
      featuredItems: featuredItemsMatches,
      freeItems: freeItemsMatches,
      featuredAuthor: featuredAuthorData,
      blocks,
      blocksB,
      blocksC,
      blocksD
    },
    router: { location },
    experiments
  } = state

  const experimentsReady = experiments.experimentsReady

  let newestItems: IItem[] = []
  let loadingCachedCategory = false

  if (newestItemMatches) {
    const newestItemMatchesForPendingCategory =
      newestItemsPendingCategory &&
      newestItemMatches[newestItemsPendingCategory]
    loadingCachedCategory = !!newestItemMatchesForPendingCategory
    const newestItemMatchesForCategory =
      newestItemMatchesForPendingCategory ||
      newestItemMatches[newestItemsCategory]
    newestItems =
      (newestItemMatchesForCategory &&
        newestItemMatchesForCategory.matches.map(match =>
          decorateSearchResult(state, match)
        )) ||
      []
  }

  const featuredItems =
    (featuredItemsMatches &&
      featuredItemsMatches.matches.map(match =>
        decorateSearchResult(state, match)
      )) ||
    []
  const freeItems =
    (freeItemsMatches &&
      freeItemsMatches.matches.map(match =>
        decorateSearchResult(state, match)
      )) ||
    []

  const featuredAuthor = (featuredAuthorData && {
    matches: featuredAuthorData.matches.map(match =>
      decorateSearchResult(state, match)
    ),
    author: featuredAuthorData.author
  }) || { matches: [] }

  const newRoutesEnabled = hasFeature(
    'routes:new',
    currentSite,
    location,
    experiments
  )

  const decoratedBlocks = decorateBlocks(state, blocks)
  const decoratedBlocksB = decorateBlocks(state, blocksB)
  const decoratedBlocksC = decorateBlocks(state, blocksC)
  const decoratedBlocksD = decorateBlocks(state, blocksD)

  return {
    currentSite,
    categories,
    metadata,
    blocks: decoratedBlocks,
    blocksB: decoratedBlocksB,
    blocksC: decoratedBlocksC,
    blocksD: decoratedBlocksD,
    isSignedIn,
    experiments,
    experimentsReady,
    newestItemsData: {
      loadingCachedCategory,
      items: newestItems,
      itemsCategory: newestItemsCategory,
      itemsPendingCategory: newestItemsPendingCategory
    },
    featuredItems,
    freeItems,
    preferences,
    featuredAuthor,
    reportItemClick: (index: number, item: IItem, listName: string) => {
      return reportProductClick({ index, item, currentSite, listName })
    },
    makeSearchLink: newRoutesEnabled ? makeSearchLink : makeLink
  }
}

const mapDispatchToProps = (dispatch: Dispatch<IRootState>): IDispatchProps => {
  return {
    fetchNewestItemsByCategory: (category: string) =>
      dispatch(fetchNewestItemsByCategory(category) as any),
    muteVideo: () => dispatch(setUserVideoPlayerStatus(true))
  }
}

export type Props = IOwnProps & IDispatchProps & IStateProps

const createHomePageContainer = (component: React.ComponentType<Props>) => {
  const pageComponent: IPageComponent<Props> = component as IPageComponent<
    Props
  >

  return connect<IStateProps, IDispatchProps, IOwnProps, IRootState>(
    mapStateToProps,
    mapDispatchToProps
  )(pageComponent)
}

export default createHomePageContainer
